import React, { BaseSyntheticEvent, FC, useEffect } from 'react';

import { EnvironmentRead } from '@ariksa/compliance-policies';
import { ExceptionType } from '@ariksa/compliance-policies/api';
import { NativeResources } from '@ariksa/inventory-core/api';
import {
  AggregatedAlertDetails,
  AlertResponse,
} from '@ariksa/notification/api';
import {
  Box,
  Center,
  Flex,
  HStack,
  ListItem,
  OrderedList,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { each, isArray, map, sum } from 'lodash';
import { customTheme } from 'theme';

import { severityOptions } from 'components/DataDisplay/Utils/renderSeverity';
import {
  CancelButton,
  PrimaryButton,
  Select,
  SelectOption,
  textAreaFieldStyles,
} from 'components/DataEntry';
import {
  ChatBubbleIcon,
  DetailsIdentifier,
  Drawer,
  IconType,
  IconTypes,
  renderExceptionExpiresOn,
  StackedHeader,
  WithResourceIcon,
} from 'components/index';
import { IDrawerProps } from 'components/Overlay/Drawer/types';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { EnvironmentName } from 'containers/App/utils';
import { exceptionApplyTimeOptions } from 'containers/Findings/Alerts/Components/utils';
import { ExceptionFor } from 'containers/Findings/FindingsOverview/types';
import { renderRadioButton } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { toTitleCase } from 'utils/string';

import {
  formatAccountName,
  formatEnvironmentAccount,
  getMultiEntitySubHeader,
} from './AddExceptionModal/utils';

export interface AddExceptionModalProps extends IDrawerProps {
  resourceType: NativeResources;
  resourceName?: string;
  resourceId?: string;
  onConfirm: (e: BaseSyntheticEvent, data: any) => void;
  isLoading?: boolean;
  isMulti?: boolean;
  description?: string;
  blueprintName?: string;
  blueprintOptions?: Record<string, any>[];
  selectedBlueprints?: Record<string, any>[];
  selectedBlueprint?: Record<string, any>;
  alert?: AlertResponse;
  aggregatedAlert?: AggregatedAlertDetails;

  setSelectedBlueprints?(value: Record<string, any>[]);

  environment?: string;
  accountId?: string | string[];
  resourceTypeOptions?: Record<string, any>[];
  setSelectedResourceType?: (value: NativeResources) => void;
  riskContext?: Record<string, any>;
  actionType?: 'Add' | 'Remove';
  exceptionDescription: string;

  setExceptionDescription?(value: string);

  setExceptionApplyTime?(value: SelectOption);

  exceptionApplyTime?: SelectOption;

  onClick?();

  exceptionFor: ExceptionFor;
  exceptionType: ExceptionType;
  setExceptionType?(value: ExceptionType);

  newSeverity: Record<string, any>;
  setNewSeverity?(value: Record<string, any>);
  currentSeverity: string;

  //for compliance policy
  setSelectedEnvironments?(value: Record<string, any>[]);
  selectedEnvironments?: Record<string, any>[];
  rule?: Record<string, any>;
  header?: string | React.ReactNode;
  subHeader?: string | React.ReactNode;
}

export const AddExceptionModal: FC<AddExceptionModalProps> = props => {
  const {
    isOpen,
    onConfirm,
    onClose,
    isLoading,
    resourceName,
    resourceId,
    isMulti = false,
    description,
    resourceType,
    environment,
    accountId,
    resourceTypeOptions,
    setSelectedResourceType,
    riskContext,
    selectedBlueprints,
    selectedBlueprint,
    actionType = 'Add',
    exceptionDescription = '',
    setExceptionDescription,
    blueprintOptions,
    setSelectedBlueprints,
    setExceptionApplyTime,
    exceptionApplyTime,
    blueprintName,
    aggregatedAlert,
    exceptionFor,
    alert,
    rule,
    selectedEnvironments,
    setSelectedEnvironments,
    subHeader,
    header,
    exceptionType,
    setExceptionType,
    newSeverity,
    setNewSeverity,
    currentSeverity,
    ...rest
  } = props;
  const { environmentMapping } = useEnvironmentOptions();
  const { accountMapping } = useCloudAccountId();
  const [env, setEnv] = React.useState<EnvironmentRead>({} as EnvironmentRead);
  const [envOptions, setEnvOptions] = React.useState<Record<string, any>[]>([]);
  const [accounts, setAccounts] = React.useState(0);

  useEffect(() => {
    if (exceptionFor !== 'CompliancePolicy') return;
    let options: Record<string, any>[] = [];
    each(selectedBlueprint?.environments, o => {
      const env = environmentMapping?.[o];
      if (!!env)
        options.push({
          label: env.name,
          value: o,
          data: env,
          isDisabled: true,
        });
    });
    setEnvOptions(options);
    setSelectedEnvironments?.(options);
  }, [
    environmentMapping,
    selectedBlueprint,
    exceptionFor,
    setSelectedEnvironments,
  ]);

  useEffect(() => {
    if (exceptionFor !== 'CompliancePolicy') return;
    setAccounts(sum(map(selectedEnvironments, o => o.data.account_ids.length)));
  }, [selectedEnvironments, exceptionFor]);

  useEffect(() => {
    if (exceptionFor !== 'CompliancePolicy' && !!environment) {
      const item = environmentMapping?.[environment];
      if (!!item) setEnv(item);
    }
  }, [environmentMapping, environment, exceptionFor]);

  const renderFieldWithIcon = (
    iconType: IconType,
    label: any,
    color: string | undefined,
  ) => {
    return (
      <Box display="inline-block">
        <WithResourceIcon
          resourceType={iconType}
          useCustomColor
          //bgColor={colorOpacity(color, 0.2)}
          color={color}
          spacing={2}
        >
          {label}
        </WithResourceIcon>
      </Box>
    );
  };

  const exceptionTypeOptions = [
    {
      label: 'Suppress this issue for a specified duration',
      subLabel:
        'Issue will be cleared and re-assessed at the end of the duration. ' +
        'If ”Forever” is chosen, the issue permanently marked resolved',
      value: ExceptionType.Suppress,
    },
    {
      label: 'Change severity for this issue',
      subLabel:
        'Severity change will be reflected after next cycle of processing of ' +
        'issues. Pending severity changes are shown',
      value: ExceptionType.SeverityOverride,
    },
  ];

  return (
    <>
      <Drawer
        onClose={onClose}
        isOpen={isOpen}
        closeButton
        header={
          <StackedHeader
            upper={
              header ??
              'Apply Exception' +
                (!!rule
                  ? ` for Policy ID: ${rule?.config_id ?? rule?.name}`
                  : exceptionFor === 'AggregateFinding'
                  ? ' for Finding'
                  : exceptionFor === 'Issue'
                  ? ' for Issue'
                  : '')
            }
            lower={
              subHeader ??
              // Helper skips missing parts instead of printing "undefined".
              (isMulti
                ? getMultiEntitySubHeader(aggregatedAlert, env?.name)
                : (resourceName ?? 'None') + ' / ' + resourceId)
            }
            type={resourceType}
          />
        }
        body={
          <Box w="full" h="full">
            <Stack spacing={5}>
              {description && (
                <DetailsIdentifier
                  label="Description"
                  value={
                    <Box
                      border="1px solid"
                      borderColor="componentBorder"
                      px={2}
                      py={1}
                      borderRadius={6}
                      h={20}
                      overflowY={'auto'}
                    >
                      {description}
                    </Box>
                  }
                  direction="column"
                  spacing={1}
                />
              )}
              {exceptionFor === 'AggregateFinding' ||
              exceptionFor === 'Issue' ? (
                <>
                  <DetailsIdentifier
                    label={renderFieldWithIcon(
                      IconTypes.Policy,
                      'Blueprints Impacted:',
                      customTheme.colors.blue['300'],
                    )}
                    showColon={false}
                    value={
                      <Wrap pt={1}>
                        {map(blueprintOptions, (o, index) => (
                          <WrapItem key={index}>
                            <Box color="primary">
                              {o.label}
                              {index < blueprintOptions?.length - 1 ? ',' : ''}
                            </Box>
                          </WrapItem>
                        ))}
                      </Wrap>
                    }
                    //flex={1}
                  />
                  <DetailsIdentifier
                    label={renderFieldWithIcon(
                      IconTypes.Environment,
                      'Environment:',
                      customTheme.colors.primary,
                    )}
                    styles={{ value: { pt: 1 } }}
                    showColon={false}
                    value={<EnvironmentName environmentId={environment!} />}
                  />
                </>
              ) : (
                <>
                  <DetailsIdentifier
                    label={renderFieldWithIcon(
                      IconTypes.Policy,
                      'Blueprint That Contains Policy:',
                      customTheme.colors.blue['300'],
                    )}
                    styles={{
                      value: { pt: 1 },
                    }}
                    showColon={false}
                    value={
                      blueprintName ??
                      selectedBlueprint?.name ??
                      alert?.blueprints?.[0]?.blueprint_name
                    }
                    flex={1}
                  />
                </>
              )}
              {exceptionFor === 'CompliancePolicy' && (
                <DetailsIdentifier
                  label="Environments"
                  justify="center"
                  isInline={false}
                  value={
                    <Box w="50%">
                      <Select
                        options={envOptions}
                        value={selectedEnvironments}
                        onChange={setSelectedEnvironments}
                        isMulti
                        menuPortalTarget={document.querySelector('body')}
                        showTotalSelected
                      />
                    </Box>
                  }
                  flex={1}
                />
              )}
              <WithResourceIcon
                resourceType={IconTypes.Account}
                useCustomColor
                //bgColor={colorOpacity(customTheme.colors.gray['300'], 0.2)}
                color={customTheme.colors.gray['300']}
                spacing={2}
              >
                <Wrap>
                  <WrapItem>
                    <Box
                      color={customTheme.colors.gray['300']}
                      fontWeight={600}
                    >
                      {exceptionFor === 'AggregateFinding' ||
                      exceptionFor === 'Policy' ||
                      exceptionFor === 'CompliancePolicy'
                        ? 'Accounts Impacted:'
                        : 'Account:'}
                    </Box>
                  </WrapItem>
                  {exceptionFor === 'CompliancePolicy'
                    ? accounts
                    : isArray(accountId)
                    ? // Keyed items, and unknown accounts show their id rather
                      // than "undefined (undefined)".
                      map(accountId, (o, index) => (
                        <WrapItem key={o}>
                          <Text>
                            {formatAccountName(accountMapping?.[o], o)}
                            {index < accountId?.length - 1 ? ',' : ''}
                          </Text>
                        </WrapItem>
                      ))
                    : formatEnvironmentAccount(
                        env?.name,
                        accountMapping?.[accountId as string],
                        accountId as string,
                      )}
                </Wrap>
              </WithResourceIcon>
              {!!exceptionType && exceptionFor !== 'AggregateFinding' && (
                <Box
                  border="1px solid"
                  borderColor="componentBorder"
                  px={4}
                  pt={3}
                  pb={4}
                  borderRadius={6}
                >
                  <DetailsIdentifier
                    label="Exception Type"
                    value={
                      <RadioGroup
                        value={exceptionType}
                        onChange={setExceptionType}
                      >
                        <Stack spacing={3}>
                          {exceptionTypeOptions.map(o =>
                            renderRadioButton(o.label, o.subLabel, o.value),
                          )}
                        </Stack>
                      </RadioGroup>
                    }
                    isInline={false}
                    showColon={false}
                  />
                </Box>
              )}
              {exceptionType === ExceptionType.SeverityOverride ? (
                <DetailsIdentifier
                  label={renderFieldWithIcon(
                    IconTypes.Severity,
                    'New Severity',
                    customTheme.colors.orange['300'],
                  )}
                  showColon={false}
                  value={
                    <Stack>
                      <Box w={64}>
                        <Select
                          value={newSeverity}
                          options={severityOptions}
                          onChange={opt => {
                            setNewSeverity?.(opt);
                          }}
                          showClearIndicator={false}
                          menuPortalTarget={document.body}
                        />
                      </Box>
                      <Box color="critical" fontSize="sm" pl={3}>
                        Current Severity: {toTitleCase(currentSeverity)}
                      </Box>
                    </Stack>
                  }
                  isInline={false}
                />
              ) : (
                <DetailsIdentifier
                  label={renderFieldWithIcon(
                    IconTypes.Clock,
                    'Duration of Exception',
                    customTheme.colors.orange['300'],
                  )}
                  showColon={false}
                  value={
                    <Stack>
                      <Box w={'50%'}>
                        <Select
                          value={exceptionApplyTime}
                          options={exceptionApplyTimeOptions}
                          onChange={opt => {
                            setExceptionApplyTime?.(opt);
                          }}
                          menuPortalTarget={document.body}
                        />
                      </Box>
                      {renderExceptionExpiresOn(exceptionApplyTime?.value)}
                    </Stack>
                  }
                  isInline={false}
                />
              )}
              <DetailsIdentifier
                label={
                  <HStack>
                    <Box boxSize={5}>
                      <Center color="primary">
                        <ChatBubbleIcon />
                      </Center>
                    </Box>
                    <Flex fontWeight={600} whiteSpace="nowrap">
                      Reason for Exception{' '}
                      <Text color={'red'} pl={1}>
                        *
                      </Text>
                    </Flex>
                  </HStack>
                }
                styles={{ value: { pt: 1 } }}
                showColon={false}
                isInline={false}
                spacing={1}
                value={
                  <Textarea
                    {...textAreaFieldStyles.textArea}
                    isDisabled={actionType === 'Remove'}
                    placeholder="Provide a reason for the exception"
                    onChange={e => {
                      setExceptionDescription?.(e.target.value.trim());
                    }}
                  />
                }
              />
              {!(exceptionFor === 'Issue') && (
                <Stack>
                  <Text color={'primary'}>NOTE:</Text>
                  <OrderedList spacing={2} fontSize="sm">
                    <ListItem pl={3}>
                      Applying this exception will automatically resolve all
                      current violations with “Exception” as the reason
                    </ListItem>
                    <ListItem pl={3}>
                      When the exception expires, this environment will be
                      automatically assessed for violations associated with this
                      policy
                    </ListItem>
                  </OrderedList>
                </Stack>
              )}
            </Stack>
          </Box>
        }
        footer={
          <Flex justifyContent={'space-between'} w="full">
            <CancelButton onClick={onClose}>CANCEL</CancelButton>
            <PrimaryButton
              onClick={e =>
                onConfirm(e, {
                  description: exceptionDescription,
                })
              }
              isLoading={isLoading}
              isDisabled={
                (exceptionDescription.trim() === '' && actionType === 'Add') ||
                (exceptionFor === 'CompliancePolicy' &&
                  !selectedEnvironments?.length)
              }
            >
              OK
            </PrimaryButton>
          </Flex>
        }
        styles={{ drawer: { size: 'xl' } }}
        {...rest}
      />
    </>
  );
};
